import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomFieldGroupType, CustomFieldType, LinkedCustomFormObjectType } from 'components/forms/custom_fields/CustomFieldTypes';
import CustomFieldBuilder from '../../components/forms/custom_fields/CustomFieldBuilder';
import FormPreview from './FormPreview';
import { useModal } from 'components/modals/ModalContext';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useFetchData } from 'services/api/useFetchData';
import { saveData } from 'services/api/saveData';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { validateCustomFieldEditor } from 'components/forms/custom_fields/customFieldValidations';
import TextInput from 'components/forms/basefields/TextInput';
import DeleteModal from 'components/modals/DeleteModal';
import '../../style/scss/detailpage.scss';
import '../../style/scss/custom-fields.scss';
import IconButton from 'components/buttons/IconButton';

const FormBuilder: React.FC<{ template?: boolean }> = ({ template = false }) => {
    const history = useHistory();
    const { t } = useTranslation();
    const { id } = useParams<{ id?: string }>();
    const location = useLocation();
    const { initializeModal } = useModal();
    const searchParams = new URLSearchParams(location.search);
    const templateId = searchParams.get('template_id');
    const [isEditingName, setIsEditingName] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [groups, setGroups] = useState<CustomFieldGroupType[]>([]); 
    const [fields, setFields] = useState<CustomFieldType[]>([]); 
    const [buttonLoader, setButtonLoader] = useState(false);
    const [deleteButtonLoader, setDeleteButtonLoader] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    // Determine wheter the form is new
    const isNew = !id;

    // Determine the api object, linked object type and value
    const apiObject = template ? 'form_template' : 'form';
    const linkedObjectType: LinkedCustomFormObjectType = template ? 'form_template' : 'form';
    const linkedObjectValue = id ?? 'temp';

    // Determine the fetch api url
    const fetchApiUrl = useMemo(() => {
        if (id) return `get_form/${apiObject}/${id}`;
        if (!template && templateId) return `get_form/form_template/${templateId}`;
        return '';
    }, [id, apiObject, template, templateId]);

    const fetchGuardValue = id ?? templateId ?? null;

    // Fetch the data
    const { response, loading } = useFetchData({
        apiUrl: fetchApiUrl,
        useGuard: true,
        guardValue: fetchGuardValue,
    });

    // Set the fetched data
    useEffect(() => {
        if (response) {
            if (isNew && templateId && !template) {
                setName('');
                setDescription(response.description ?? '');
            } else {
                setName(response.name ?? '');
                setDescription(response.description ?? '');
            }

            setGroups(response.groups ?? []);
            setFields(response.fields ?? []);
        }
    }, [response, isNew, templateId, template]);

    // Determine the header title
    const headerTitle = useMemo(() => {
        if (template && isNew) {
            return 'form.builder.set_up_template_title';
        }

        if (template) {
            return 'form.builder.edit_template_title';
        }

        if (isNew) {
            return 'form.builder.set_up_form_title';
        }

        return 'form.builder.edit_form_title';
    }, [template, isNew]);

    // Render the name field
    const renderNameField = () => {
        if (isNew) {
            return (
                <TextInput
                    name='name'
                    value={name}
                    placeholder={template ? 'Naam sjabloon' : 'Naam formulier'}
                    shouldAutoFocus={true}
                    onChange={(value) => setName(value)}
                />
            );
        }

        if (isEditingName) {
            return (
                <TextInput
                    name='name'
                    value={name}
                    placeholder={template ? 'Naam sjabloon' : 'Naam formulier'}
                    shouldAutoFocus={true}
                    onChange={(value) => setName(value)}
                    onBlur={() => setIsEditingName(false)}
                />
            );
        }

        return (
            <div
                className='onclick-live-edit-field'
                onClick={() => setIsEditingName(true)}
            >
                <p>
                    {name || (template ? 'Naam sjabloon' : 'Naam formulier')}
                </p>
                <span className='edit-icon'>
                    <FontAwesomeIcon icon={faPen} />
                </span>
            </div>
        );
    };

    // Handle save
    const handleSave = async () => {

        // Validate the required fields
        const errors = validateCustomFieldEditor({groups, fields, supportsGroups: true, t});
        if (errors.length > 0) {
            setValidationErrors(errors);
            return;
        }

        // Otherwise reset errors
        setValidationErrors([]);

        // Start button loader
        setButtonLoader(true);

        try {
            // Generate the groups to submit
            const submitGroups = groups.map(group => ({
                ...group,
                linked_object_type: linkedObjectType,
                linked_object_value: linkedObjectValue,
            }));

            // Generate the fields to submit, parents before their conditional questions
            const submitFields = fields
                .map(field => ({
                    ...field,
                    linked_object_type: linkedObjectType,
                    linked_object_value: linkedObjectValue,
                }))
                .sort((firstField, secondField) =>
                    Number(Boolean(firstField.parent_field_id)) - Number(Boolean(secondField.parent_field_id))
                );

            // Send the backend request
            await saveData({
                apiUrl: isNew ? `post_form/${apiObject}` : `patch_form/${apiObject}/${id}`,
                method: isNew ? 'post' : 'patch',
                data: {
                    name,
                    description,
                    groups: submitGroups,
                    fields: submitFields,
                },
            });

            // Redirect to list after saving
            history.push(template ? '/settings/custom-fields/forms' : '/settings/custom-fields/forms');
        } catch (error) {
            console.error(error);
        } finally {
            setButtonLoader(false);
        }
    };

    // Handle delete
    const handleOpenDeleteModal = () => {
        if (!isNew && id) {
            initializeModal(
                <DeleteModal
                    patchToServer={{
                        apiObject: `form/${apiObject}`,
                        itemId: id,
                    }}
                    onConfirmDelete={() => history.push('/settings/custom-fields/forms')}
                />,
                { modalSize: 'extra-small' }
            );
        }
    };

    return (
        <div className='detailpage container-wide form-builder'>
            <div className='detailpage-header row'>
                <div className='header-title'>
                    <span 
                        className='back-button'
                        onClick={() => history.goBack()}
                    >
                        <FontAwesomeIcon icon={faAngleLeft} />
                    </span>
                    <h2 className='fixed-title'>
                        {isNew ? t(headerTitle) : renderNameField()}
                    </h2>
                </div>
                <div className='button-container right-align'>
                    {!isNew && (
                        <IconButton
                            tooltipText='general.delete'
                            icon={<FontAwesomeIcon icon={faTrash} />}
                            onClick={handleOpenDeleteModal}
                        />
                    )}
                    <SecondaryButton
                        label='general.cancel'
                        onClick={() => history.goBack()}
                    />
                    <PrimaryButton
                        label='general.save'
                        loading={buttonLoader}
                        onClick={handleSave}
                    />
                </div>
            </div>
            {validationErrors.length > 0 && (
                <div className='custom-field-validation-errors'>
                    {validationErrors.map((validation_error, index) => (
                        <p key={index}>
                            {validation_error}
                        </p>
                    ))}
                </div>
            )}
            <div className='form-builder-detail-fields'>
                {isNew && (
                    renderNameField()
                )}
                {/* <TextareaInput
                    name='description'
                    value={description}
                    placeholder={template ? 'Omschrijving formulier sjabloon' : 'Omschrijving formulier'}
                    onChange={(value) => setDescription(typeof value === 'string' ? value : value.target.value)}
                /> */}
            </div>
            {(isNew || loading === 'success') && (
                <div className='form-builder-layout'>
                    <CustomFieldBuilder
                        groups={groups}
                        setGroups={setGroups}
                        fields={fields}
                        setFields={setFields}
                        supportsGroups={true}
                        extendedOptionsFeature={'forms_extended'}
                        selectedLinkedObjectType={linkedObjectType}
                        selectedLinkedObjectValue={linkedObjectValue}
                        showFieldPreview={false}
                    />

                    <FormPreview
                        formName={name}
                        groups={groups}
                        fields={fields}
                    />
                </div>
            )}
        </div>
    );
};

export default FormBuilder;